import { BoxComponent } from '../../../base/box/Box';
import { BaseContainer } from '../../../base/container/BaseContainer';
import CtaButton from '../../../components/CtaButton';
import { heroVariantOneInitialProps, resolveSectionBackgroundColor } from '../../Commons';
import { EditableContent } from '../../SharedComponents';
import { HeroVariantOneProps } from '../types/index';
import { styles } from './HeroSectionVariantOne.styles';
import { useHeroState } from '../hooks/useHeroState';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';
import {
  createButtonStyles,
  createSecondaryButtonStyles,
  createSectionHeightStyles,
  createBackgroundOverlayStyles,
  createPositionStyles,
} from '../utils/heroUtils';
import { HeroBackground } from '../components/HeroBackground';
import AnimateOnView from '../../../components/AnimateOnView';

const HeroSectionVariantOne = ({
  globalStyles = heroVariantOneInitialProps.globalStyles,
  localStyles = heroVariantOneInitialProps.localStyles,
  componentConfig = heroVariantOneInitialProps.componentConfig,
  builderConfig = heroVariantOneInitialProps.builderConfig,
  setComponentData,
  MediaMap,
  ImageComponent,
  onTextSelect,
}: HeroVariantOneProps) => {
  const { builderMode, previewMode, isBuilderDesktopMode, isPublishedDesktopMode, pageUrls, seoTitle } = builderConfig;

  const designPreview = !builderConfig?.builderMode && builderConfig?.previewMode;

  const {
    video,
    image,
    carousel,
    heading,
    button,
    subheading,
    bodyText,
    overline,
    variant,
    sectionHeight,
    contentPosition,
    secondaryButton,
    overlayStyles,
    customHeight = 0,
  } = componentConfig;

  const backgroundColor = resolveSectionBackgroundColor(
    {
      background: { value: componentConfig?.overlayStyles?.overlayColor },
      backgroundImage: { toggle: true, value: 'image', overlayColor: componentConfig?.overlayStyles?.overlayColor },
    },
    globalStyles
  );
  // Custom hooks
  const { currentIndex, isAutoPlay, getCurrentIndex, getCurrentActiveState } = useHeroState();
  const isDesktop = Boolean(isBuilderDesktopMode || isPublishedDesktopMode);
  const { createResponsiveStyles } = useResponsiveStyles(isDesktop, localStyles, globalStyles);

  // Create styles using utility functions
  const buttonStyles = createButtonStyles(button, globalStyles, localStyles, isDesktop);
  const secondaryButtonStyles = secondaryButton
    ? createSecondaryButtonStyles(secondaryButton, globalStyles, localStyles, isDesktop)
    : {};
  const sectionHeightStyles = createSectionHeightStyles(
    sectionHeight,
    isDesktop,
    customHeight,
    builderMode,
    designPreview || false
  );
  const backgroundOverlayStyles = createBackgroundOverlayStyles(overlayStyles);
  const headingContainerStyles = {
    ...styles.HeadingContainer,
    ...createPositionStyles(contentPosition),
  };

  const buttonWidthStyles = {
    ...buttonStyles,
    ...(!isDesktop && button?.fullWidth ? { width: '100%', textAlign: 'center' } : {}),
  };

  const secondaryButtonWidthStyles = {
    ...secondaryButtonStyles,
    ...(!isDesktop && secondaryButton?.fullWidth ? { width: '100%', textAlign: 'center' } : {}),
  };
  // Create responsive text styles using styles file

  const headingTextStyles = {
    ...styles.Heading,
    ...globalStyles.heading,
    ...localStyles.heading,
  };

  const subHeadingTextStyles = {
    ...styles.HeroSectionSubHeading,
    ...globalStyles.subHeading,
    ...localStyles.subheading,
  };

  const bodyTextStyles = {
    ...styles.HeroSectionParagraph,
    ...globalStyles.bodyText,
    ...localStyles.bodyText,
  };

  const overlineTextStyles = {
    ...styles.HeroSectionOverline,
    ...globalStyles.bodyText,
    ...localStyles.overline,
  };

  // Get mobile styles for builder preview mode
  const headingTextStylesResponsive = createResponsiveStyles(headingTextStyles, 'heading');
  const subheadingTextStylesResponsive = createResponsiveStyles(subHeadingTextStyles, 'subheading');
  const bodyTextStylesMobile = createResponsiveStyles(bodyTextStyles, 'bodyText');
  const overlineTextStylesMobile = createResponsiveStyles(overlineTextStyles, 'overline');

  const heroContainerStyles = {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: isDesktop ? '0 100px' : '0',
    '@media (max-width: 780px)': {
      padding: '0',
    },
    ...sectionHeightStyles,
  };

  const handleTextSelect = (dataKey: string) => (data: any) => {
    if (onTextSelect && data) {
      onTextSelect({ ...data, dataKey });
    } else if (onTextSelect && data === null) {
      onTextSelect(null);
    }
  };

  const headingDataKey = variant === 'CAROUSEL' ? `carousel.value.${currentIndex.toString()}.heading` : 'heading.text';
  const overlineDataKey =
    variant === 'CAROUSEL' ? `carousel.value.${currentIndex.toString()}.overline` : 'overline.text';
  const subheadingDataKey =
    variant === 'CAROUSEL' ? `carousel.value.${currentIndex.toString()}.subheading` : 'subheading.text';
  const bodyTextDataKey =
    variant === 'CAROUSEL' ? `carousel.value.${currentIndex.toString()}.bodyText` : 'bodyText.text';

  return (
    <BoxComponent>
      <BoxComponent sx={styles.HeroWrapper} id="1">
        <BoxComponent
          sx={{ ...heroContainerStyles, paddingTop: isDesktop ? '0' : '50px', paddingBottom: isDesktop ? '0' : '50px' }}
        >
          {/* Background media - absolute positioned */}
          <HeroBackground
            variant={variant}
            video={video}
            image={image}
            carousel={carousel}
            builderMode={builderMode || false}
            MediaMap={MediaMap}
            ImageComponent={ImageComponent}
            sectionHeightStyles={sectionHeightStyles}
            localStyles={localStyles}
            altText={image.altText || seoTitle || image.name}
            currentIndex={currentIndex}
            isAutoPlay={isAutoPlay}
            getCurrentIndex={getCurrentIndex}
            getCurrentActiveState={getCurrentActiveState}
            overlayStyles={overlayStyles}
            onBackgroundClick={
              onTextSelect
                ? () => {
                    onTextSelect({ text: '', range: { start: 0, end: 0 }, dataKey: 'background' });
                  }
                : undefined
            }
          />
          {/* Overlay - only for non-carousel variants, absolute positioned, sibling of background */}
          {/* For carousel, overlay is handled inside HeroBackground component */}
          {variant !== 'CAROUSEL' && <BoxComponent id="2" sx={backgroundOverlayStyles} />}
          {/* Content - relative positioned */}
          <BaseContainer
            className="section-content"
            sx={{
              ...headingContainerStyles,
              position: 'relative',
              zIndex: 2,
            }}
          >
            <AnimateOnView
              animateOnMount={false}
              animateOnScroll={true}
              previewMode={previewMode}
              builderMode={builderMode}
              globalStyles={globalStyles}
              sx={{ width: '100%' }}
            >
              {overline?.toggle && (
                <EditableContent
                  dataKey={overlineDataKey}
                  sx={{
                    ...overlineTextStylesMobile,
                    inlineText:
                      variant === 'CAROUSEL'
                        ? localStyles?.carousel?.value?.[currentIndex]?.overline?.inlineText
                        : localStyles?.overline?.inlineText,
                  }}
                  previewMode={previewMode}
                  builderMode={builderMode || false}
                  setComponentData={setComponentData}
                  getCurrentActiveState={getCurrentActiveState}
                  text={variant === 'CAROUSEL' ? carousel.value[currentIndex]?.overline : overline?.text}
                  Element="body1"
                  onTextSelect={handleTextSelect(overlineDataKey)}
                  isDesktop={isDesktop}
                  backgroundColor={backgroundColor}
                  textAlignment={contentPosition}
                />
              )}
              {heading?.toggle && (
                <EditableContent
                  dataKey={headingDataKey}
                  sx={{
                    ...headingTextStylesResponsive,
                    inlineText:
                      variant === 'CAROUSEL'
                        ? localStyles?.carousel?.value?.[currentIndex]?.heading?.inlineText
                        : localStyles?.heading?.inlineText,
                  }}
                  previewMode={previewMode}
                  builderMode={builderMode || false}
                  setComponentData={setComponentData}
                  getCurrentActiveState={getCurrentActiveState}
                  text={variant === 'CAROUSEL' ? carousel.value[currentIndex]?.heading : heading.text}
                  Element="h1"
                  onTextSelect={handleTextSelect(headingDataKey)}
                  isDesktop={isDesktop}
                  backgroundColor={backgroundColor}
                  textAlignment={contentPosition}
                />
              )}
              {subheading?.toggle && (
                <EditableContent
                  dataKey={subheadingDataKey}
                  sx={{
                    ...subheadingTextStylesResponsive,
                    inlineText:
                      variant === 'CAROUSEL'
                        ? localStyles?.carousel?.value?.[currentIndex]?.subheading?.inlineText
                        : localStyles?.subheading?.inlineText,
                  }}
                  previewMode={previewMode}
                  builderMode={builderMode || false}
                  setComponentData={setComponentData}
                  text={variant === 'CAROUSEL' ? carousel.value[currentIndex]?.subheading : subheading.text}
                  getCurrentActiveState={getCurrentActiveState}
                  Element="body1"
                  textAlignment={contentPosition}
                  onTextSelect={handleTextSelect(subheadingDataKey)}
                  isDesktop={isDesktop}
                  backgroundColor={backgroundColor}
                />
              )}
              {bodyText && bodyText.toggle && (
                <EditableContent
                  dataKey={bodyTextDataKey}
                  sx={{
                    ...bodyTextStylesMobile,
                    inlineText:
                      variant === 'CAROUSEL'
                        ? localStyles?.carousel?.value?.[currentIndex]?.bodyText?.inlineText
                        : localStyles?.bodyText?.inlineText,
                  }}
                  previewMode={previewMode}
                  builderMode={builderMode || false}
                  setComponentData={setComponentData}
                  text={variant === 'CAROUSEL' ? carousel.value[currentIndex]?.bodyText : bodyText.text}
                  getCurrentActiveState={getCurrentActiveState}
                  Element="body1"
                  onTextSelect={handleTextSelect(bodyTextDataKey)}
                  isDesktop={isDesktop}
                  backgroundColor={backgroundColor}
                  textAlignment={contentPosition}
                />
              )}
              <BoxComponent
                sx={
                  isDesktop
                    ? { ...styles.btnContainer, ...createPositionStyles(contentPosition), padding: 0 }
                    : styles.btnContainer_Mobile
                }
              >
                {button?.toggle && (
                  <BoxComponent
                    sx={{
                      display: 'inline-flex',
                      width: isDesktop ? 'auto' : '100%',
                      justifyContent: isDesktop ? 'flex-start' : 'center',
                    }}
                  >
                    <CtaButton
                      builderMode={builderMode || false}
                      previewMode={previewMode}
                      buttonConfig={button}
                      buttonStyles={buttonWidthStyles}
                      pageUrls={pageUrls}
                      onButtonClick={
                        onTextSelect
                          ? (dataKey: string) => {
                              onTextSelect({ text: '', range: { start: 0, end: 0 }, dataKey });
                            }
                          : undefined
                      }
                      dataKey="button.text"
                    />
                  </BoxComponent>
                )}
                {secondaryButton?.toggle && (
                  <BoxComponent
                    sx={{
                      display: 'inline-flex',
                      width: isDesktop ? 'auto' : '100%',
                      justifyContent: isDesktop ? 'flex-start' : 'center',
                    }}
                  >
                    <CtaButton
                      builderMode={builderMode || false}
                      previewMode={previewMode}
                      buttonConfig={secondaryButton}
                      buttonStyles={secondaryButtonWidthStyles}
                      pageUrls={pageUrls}
                      onButtonClick={
                        onTextSelect
                          ? (dataKey: string) => {
                              onTextSelect({ text: '', range: { start: 0, end: 0 }, dataKey });
                            }
                          : undefined
                      }
                      dataKey="secondaryButton.text"
                    />
                  </BoxComponent>
                )}
              </BoxComponent>
            </AnimateOnView>
          </BaseContainer>
        </BoxComponent>
      </BoxComponent>
    </BoxComponent>
  );
};

export default HeroSectionVariantOne;
